import {
  forwardRef,
  HTMLAttributes,
  KeyboardEvent,
  ReactNode,
  useRef,
} from "react";
import styled, { css } from "styled-components";
import { TrashBinIcon } from "../../assets/icons/trashbinIcon";
import { designSystemVars as vars } from "../tokens";
import { AnchoredOverlay, AnchoredOverlayProps } from "./AnchoredOverlay";
import { Button, ButtonBase, ButtonProps } from "./Button";
import { StatusBadge, StatusBadgeIcon } from "./Badge";
import { Checkbox } from "./Form";
import { FilterToolbar } from "./Layout";

export const TableFilterBar = styled(FilterToolbar)<{
  $variant?: "standard" | "agency" | "wrapping";
}>`
  ${({ $variant }) =>
    ($variant === "agency" || $variant === "wrapping") &&
    css`
      gap: ${vars.spacing.lg};
    `}

  ${({ $variant }) =>
    $variant === "wrapping" &&
    css`
      height: auto;
      min-height: 68px;
    `}

  ${({ $variant }) =>
    $variant === "agency" &&
    css`
      > button {
        flex-shrink: 0;
      }
    `}
`;

export const TableFilterList = styled.div<{
  $variant?: "scroll" | "wrap";
}>`
  display: flex;
  min-width: 0;
  align-items: center;
  gap: ${vars.spacing.sm};

  ${({ $variant = "scroll" }) =>
    $variant === "wrap"
      ? css`
          flex-wrap: wrap;
          font-family: ${vars.typography.family.sans};
          font-size: ${vars.typography.size.sm};
          line-height: ${vars.typography.lineHeight.sm};
        `
      : css`
          flex: 1 1 auto;
          flex-wrap: nowrap;
          overflow-x: auto;
          overflow-y: hidden;
          white-space: nowrap;
        `}
`;

export const TableFilterCheckbox = styled(Checkbox)<{
  $variant?: "standard" | "compact";
}>`
  ${({ $variant }) =>
    $variant === "compact" &&
    css`
      width: ${vars.spacing.lg};
      height: ${vars.spacing.lg};
      margin: 0;
      flex-shrink: 0;
    `}
`;

const filterMenuWidths = {
  standard: "180px",
  wide: "240px",
  workflow: "229px",
  assessment: "333px",
  process: "110px",
  candidateStatus: "155px",
} as const;

export type TableFilterMenuSize = keyof typeof filterMenuWidths;

export interface TableFilterSelection<T extends string> {
  selectedOptions: T[];
  isAllSelected: boolean;
}

interface GetNextTableFilterOptionSelectionArgs<
  T extends string,
> extends TableFilterSelection<T> {
  options: readonly T[];
  option: T;
}

export const getNextTableFilterOptionSelection = <T extends string>({
  options,
  option,
  selectedOptions,
  isAllSelected,
}: GetNextTableFilterOptionSelectionArgs<T>): TableFilterSelection<T> => {
  const nextOptions = isAllSelected
    ? [option]
    : selectedOptions.includes(option)
      ? selectedOptions.filter((selected) => selected !== option)
      : [...selectedOptions, option];

  const selectsEveryOption =
    options.length > 0 &&
    options.every((availableOption) => nextOptions.includes(availableOption));

  if (nextOptions.length === 0 || selectsEveryOption) {
    return { selectedOptions: [], isAllSelected: true };
  }

  return { selectedOptions: nextOptions, isAllSelected: false };
};

export const getNextTableFilterAllSelection = <T extends string>(
  isAllSelected: boolean,
): TableFilterSelection<T> => ({
  selectedOptions: [],
  isAllSelected: !isAllSelected,
});

const StyledTableFilterMenu = styled(AnchoredOverlay)<{
  $width?: string;
  $align?: "left" | "right";
}>`
  box-sizing: border-box;
  display: flex;
  width: ${({ $width }) => $width || filterMenuWidths.standard};
  min-width: ${({ $width }) => $width || filterMenuWidths.standard};
  max-height: 400px;
  flex-direction: column;
  align-items: flex-start;
  overflow-y: auto;
  border-right: ${vars.borderWidth.medium} solid ${vars.color.border.accent};
  border-bottom: ${vars.borderWidth.medium} solid ${vars.color.border.accent};
  border-left: ${vars.borderWidth.medium} solid ${vars.color.border.accent};
  border-radius: ${vars.radius.none} ${vars.radius.none} ${vars.radius.sm}
    ${vars.radius.sm};
  background: ${vars.color.background.surface};
  padding-left: ${vars.spacing.sm};
  color: ${vars.color.text.primary};
  font-family: ${vars.typography.family.sans};
  font-size: ${vars.typography.size.md};
  font-weight: ${vars.typography.weight.regular};
  line-height: var(--inzeloop-ds-line-height-25-5px);
  box-shadow: ${vars.shadow.dropdown};

  button {
    font-family: inherit;
    font-weight: inherit;
  }

  &[data-placement="top"] {
    border-top: ${vars.borderWidth.medium} solid ${vars.color.border.accent};
    border-bottom: 0;
    border-radius: ${vars.radius.sm} ${vars.radius.sm} ${vars.radius.none}
      ${vars.radius.none};
  }
`;

export interface TableFilterMenuProps
  extends
    HTMLAttributes<HTMLDivElement>,
    Pick<
      AnchoredOverlayProps,
      | "anchorRef"
      | "matchAnchorWidth"
      | "gap"
      | "preferredPlacement"
      | "viewportPadding"
    > {
  $width?: string;
  $size?: TableFilterMenuSize;
  $align?: "left" | "right";
  onEscape?: () => void;
}

export const TableFilterMenu = forwardRef<HTMLDivElement, TableFilterMenuProps>(
  ({ onKeyDown, onEscape, $size, ...props }, forwardedRef) => {
    const localRef = useRef<HTMLDivElement | null>(null);

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
      onKeyDown?.(event);
      if (event.defaultPrevented) return;

      const options = Array.from(
        localRef.current?.querySelectorAll<HTMLButtonElement>(
          '[role="option"]:not(:disabled)',
        ) || [],
      );
      const currentIndex = options.indexOf(
        document.activeElement as HTMLButtonElement,
      );
      let nextIndex: number | undefined;

      if (event.key === "ArrowDown") {
        nextIndex = currentIndex < 0 ? 0 : (currentIndex + 1) % options.length;
      } else if (event.key === "ArrowUp") {
        nextIndex =
          currentIndex < 0
            ? options.length - 1
            : (currentIndex - 1 + options.length) % options.length;
      } else if (event.key === "Home") {
        nextIndex = 0;
      } else if (event.key === "End") {
        nextIndex = options.length - 1;
      } else if (event.key === "Escape") {
        event.preventDefault();
        onEscape?.();
        return;
      }

      if (nextIndex !== undefined && options[nextIndex]) {
        event.preventDefault();
        options[nextIndex].focus();
      }
    };

    return (
      <StyledTableFilterMenu
        {...props}
        $width={props.$width ?? ($size ? filterMenuWidths[$size] : undefined)}
        style={
          $size === "workflow"
            ? { maxHeight: "300px", ...props.style }
            : props.style
        }
        align={props.$align}
        ref={(element) => {
          localRef.current = element;
          if (typeof forwardedRef === "function") forwardedRef(element);
          else if (forwardedRef) forwardedRef.current = element;
        }}
        role="listbox"
        onKeyDown={handleKeyDown}
      />
    );
  },
);

TableFilterMenu.displayName = "TableFilterMenu";

export const TableFilterOption = styled(ButtonBase).attrs({
  type: "button",
  role: "option",
})<{ $selected?: boolean }>`
  box-sizing: border-box;
  display: flex;
  width: 100%;
  min-height: 54px;
  flex-shrink: 0;
  align-items: center;
  gap: ${vars.spacing.sm};
  padding: var(--inzeloop-ds-spacing-11px) ${vars.spacing.sm};
  border: 0;
  border-radius: ${vars.radius.xs};
  background: var(--inzeloop-ds-color-background-transparent);
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: default;

  input {
    pointer-events: none;
  }

  &:not(:disabled):hover {
    background: ${vars.color.action.secondaryHover};
  }

  &:focus-visible {
    outline: none;
    box-shadow: inset ${vars.shadow.focus};
  }

  &:disabled {
    color: ${vars.color.text.disabled};
    cursor: not-allowed;
  }
`;

export const TableFilterEmpty = styled.div`
  padding: ${vars.spacing.sm};
  color: ${vars.color.text.muted};
  font: inherit;
`;

export const TableFilterTabs = styled.div`
  display: flex;
  width: 100%;
  box-sizing: border-box;
  align-items: flex-start;
  justify-content: flex-start;
  padding: ${vars.spacing.md} 0;
`;

const TableFilterTagIcon = styled.span`
  display: inline-flex;
  width: 16px;
  height: 16px;
  align-items: center;
  justify-content: center;
  flex: 0 0 16px;

  svg {
    width: 16px;
    height: 16px;
  }

  svg [stroke] {
    stroke: currentColor;
  }
`;

const TableFilterTagLabel = styled.span`
  display: inline-flex;
  align-items: center;
  line-height: inherit;
`;

const TableFilterTagRemoveButton = styled(ButtonBase).attrs({
  type: "button",
})`
  display: inline-flex;
  width: 24px;
  height: 24px;
  align-items: center;
  justify-content: center;
  flex: 0 0 24px;
  padding: ${vars.spacing.xs};
  border: 0;
  background: ${vars.color.background.transparent};
  color: ${vars.color.action.primary};
  cursor: default;

  &:focus-visible {
    outline: none;
    box-shadow: ${vars.shadow.focus};
  }
`;

const StyledTableFilterTag = styled(StatusBadge)<{
  $compact: boolean;
  $iconStrokeWidth?: number;
}>`
  line-height: ${vars.typography.lineHeight.xs};

  ${StatusBadgeIcon} {
    box-sizing: border-box;
    width: ${vars.spacing.xl};
    height: ${vars.spacing.xl};
    flex: 0 0 ${vars.spacing.xl};
  }

  ${({ $iconStrokeWidth }) =>
    $iconStrokeWidth !== undefined &&
    css`
      svg [stroke] {
        stroke-width: ${$iconStrokeWidth}px;
        vector-effect: non-scaling-stroke;
      }
    `}

  ${({ $compact }) =>
    $compact &&
    css`
      height: ${vars.spacing.px28};
      font-size: ${vars.typography.size.xs};
      font-weight: ${vars.typography.weight.regular};
      line-height: ${vars.typography.lineHeight.sm};

      ${TableFilterTagRemoveButton} {
        width: ${vars.spacing.lg};
        height: ${vars.spacing.lg};
        flex-basis: ${vars.spacing.lg};
        padding: 0;
      }
    `}
`;

export interface TableFilterTagProps {
  label: ReactNode;
  border: string;
  padding?: string;
  background?: string;
  color?: string;
  icon?: ReactNode;
  iconBackground?: string;
  iconStrokeWidth?: number;
  variant?: "default" | "compact";
  ariaLabel?: string;
  removeLabel?: string;
  onRemove?: () => void;
}

export const TableFilterTag = ({
  label,
  border,
  padding,
  background,
  color,
  icon,
  iconBackground,
  iconStrokeWidth,
  variant = "default",
  ariaLabel,
  removeLabel,
  onRemove,
}: TableFilterTagProps) => (
  <StyledTableFilterTag
    $compact={variant === "compact"}
    $iconStrokeWidth={
      iconStrokeWidth ??
      (icon && iconBackground && variant === "default" ? 1.15 : undefined)
    }
    border={border}
    padding={
      padding ??
      (icon && iconBackground && variant === "default"
        ? `0 ${onRemove ? vars.spacing.xs : vars.spacing.md} 0 ${vars.spacing.xs}`
        : undefined)
    }
    bg={background}
    $color={color}
    $filterTag
    $filterTypography
    aria-label={ariaLabel}
  >
    {icon &&
      (iconBackground ? (
        <StatusBadgeIcon bg={iconBackground} aria-hidden="true">
          <TableFilterTagIcon>{icon}</TableFilterTagIcon>
        </StatusBadgeIcon>
      ) : (
        <TableFilterTagIcon aria-hidden="true">{icon}</TableFilterTagIcon>
      ))}
    <TableFilterTagLabel>{label}</TableFilterTagLabel>
    {onRemove && (
      <TableFilterTagRemoveButton
        onClick={onRemove}
        aria-label={removeLabel || "Remove filter"}
      >
        <TrashBinIcon size={16} />
      </TableFilterTagRemoveButton>
    )}
  </StyledTableFilterTag>
);

const TableFilterGroupTagLabel = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${vars.spacing.xs} ${vars.spacing.sm};
  font: inherit;
`;

export const TableFilterGroupTag = ({ label }: { label: ReactNode }) => (
  <StatusBadge
    border={vars.color.action.primary}
    bg={vars.color.background.transparent}
    $filterTag
    $filterTypography
  >
    <TableFilterGroupTagLabel>{label}</TableFilterGroupTagLabel>
  </StatusBadge>
);

export interface ClearFiltersButtonProps extends Omit<
  ButtonProps,
  "leadingIcon" | "variant"
> {}

export const ClearFiltersButton = forwardRef<
  HTMLButtonElement,
  ClearFiltersButtonProps
>(({ children = "Clear Filters", size = "sm", ...buttonProps }, ref) => (
  <Button
    {...buttonProps}
    ref={ref}
    size={size}
    variant="secondary"
    leadingIcon={<TrashBinIcon size={20} />}
  >
    {children}
  </Button>
));

ClearFiltersButton.displayName = "ClearFiltersButton";
